import { API_BASE_URL } from '@/environment'
import { customFetch } from '@/service/v3/apiV3Utils'
import { customFetchMedia } from '@/service/v3/apiV3UtilsMedia'
import { queryParams } from '@attensi/utils'
import type {
  CreateUserUploadParams,
  CreateUserUploadResponse,
  GetUserUploadParams,
  ListUserUploadsParams,
  UserUploadSummaryListResponse,
  UserUploadSummaryResponse,
  ValidateUserUploadAttributes,
  ValidateUserUploadParams,
  ValidateUserUploadResponse,
} from './userUploadTypes'

const API_ADMIN_NAMESPACE = 'api/v3/admin'

const buildFormData = (file: File): FormData => {
  const formData = new FormData()
  formData.append('data[attributes][file]', file)
  return formData
}

export const validateUserUpload = async ({
  companyNameKey,
  file,
}: ValidateUserUploadParams): Promise<ValidateUserUploadAttributes> => {
  const url = `${API_BASE_URL}/${API_ADMIN_NAMESPACE}/companies/${companyNameKey}/user_list_uploads/validate`

  const response = await customFetchMedia(url, {
    method: 'POST',
    body: buildFormData(file),
  })

  if (!response.ok) {
    throw new Error(`Failed to validate user upload: ${response.statusText}`)
  }

  const body = (await response.json()) as ValidateUserUploadResponse
  return body.data.attributes
}

export const createUserUpload = async ({
  companyNameKey,
  file,
}: CreateUserUploadParams): Promise<CreateUserUploadResponse> => {
  const url = `${API_BASE_URL}/${API_ADMIN_NAMESPACE}/companies/${companyNameKey}/user_list_uploads`

  const response = await customFetchMedia(url, {
    method: 'POST',
    body: buildFormData(file),
  })

  if (!response.ok) {
    throw new Error(`Failed to create user upload: ${response.statusText}`)
  }

  return response.json()
}

export const getUserUpload = async ({
  companyNameKey,
  uploadId,
}: GetUserUploadParams): UserUploadSummaryResponse => {
  const url = `${API_BASE_URL}/${API_ADMIN_NAMESPACE}/companies/${companyNameKey}/user_list_uploads/${uploadId}`

  const response = await customFetch(url, { method: 'GET' })

  if (!response.ok) {
    throw new Error(`Failed to fetch user upload: ${response.statusText}`)
  }

  return response.json()
}

export const listUserUploads = async ({
  companyNameKey,
  filterState,
  sort,
  pageSize,
  pageNumber,
}: ListUserUploadsParams): UserUploadSummaryListResponse => {
  const url = queryParams(
    `${API_BASE_URL}/${API_ADMIN_NAMESPACE}/companies/${companyNameKey}/user_list_uploads`,
    {
      'filter[state]': filterState,
      sort,
      'page[size]': pageSize,
      'page[number]': pageNumber,
    }
  )

  const response = await customFetch(url, { method: 'GET' })

  if (!response.ok) {
    throw new Error(`Failed to list user uploads: ${response.statusText}`)
  }

  return response.json()
}
